'use client';

/**
 * PassPlansSection — Hero/Tabs/Plans grid da `/pass` (Figma 189:1711).
 *
 * Comportamento (per spec PL 2026-05-16):
 *   - Headline display Bodoni Bold Italic em "brisa" + amarelo accent
 *   - Tabs pill (Individual / Família) — variant="default-pill"
 *   - 4 CardPlanComplete na aba Individual (Visitor/Basic/Premium4 highlighted/Premium10)
 *   - Aba Família = placeholder "em breve" + CTA scroll to waitlist
 *   - Preços "em breve" (pré-MVP, decisão PL 2026-05-15)
 *   - CTA "Escolher" em cada card → scroll to #waitlist
 */
import * as React from 'react';
import { motion, AnimatePresence, type Variants } from 'framer-motion';

import { Tabs, TabsList, TabsTrigger } from '@brisa/ui/components/ui/tabs';
import { CardPlanComplete } from '@brisa/ui/components/facelift/pass-card/CardPlanComplete';
import { Button } from '@brisa/ui/components/ui/button';

export interface PassPlanCopy {
  tag: string;
  price: string;
  priceSubtext: string;
  description: string;
  period: string;
  clubes: string;
  visitas: string;
}

export interface PassPlansCopy {
  titlePart1: string;
  titleBrisa: string;
  tabIndividual: string;
  tabFamilia: string;
  familiaTitle: string;
  familiaBody: string;
  familiaCta: string;
  ctaChoose: string;
  freeBadgePremium4: string;
  infoPeriodo: string;
  infoClubes: string;
  infoVisitas: string;
  plans: {
    visitor_week: PassPlanCopy;
    basic: PassPlanCopy;
    premium4: PassPlanCopy;
    premium10: PassPlanCopy;
  };
}

export interface PassPlansSectionProps {
  copy: PassPlansCopy;
  scrollTarget?: string;
  surface?: string;
}

const PLAN_ORDER: Array<keyof PassPlansCopy['plans']> = [
  'visitor_week',
  'basic',
  'premium4',
  'premium10',
];

/**
 * Variants pra orquestrar entrada do painel + stagger dos cards.
 * `when: 'beforeChildren'` garante que o cross-fade do painel finalize ANTES
 * dos cards começarem o stagger — sem isso, a opacity 0→1 do painel mascara
 * visualmente o stagger das filhas, dando impressão de que não animam.
 */
const panelVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: 'easeOut',
      when: 'beforeChildren',
      staggerChildren: 0.12,
    },
  },
  exit: { opacity: 0, y: -8, transition: { duration: 0.25 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

export function PassPlansSection({
  copy,
  scrollTarget = 'waitlist',
  surface = 'pass_plans',
}: PassPlansSectionProps) {
  const [value, setValue] = React.useState('individual');

  const scrollToWaitlist = React.useCallback(() => {
    const el = document.getElementById(scrollTarget);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollTarget]);

  return (
    <section className="relative w-full" aria-label="Planos">
      {/* Title-section: headline + tabs pill — motion de entrada no mount */}
      <div className="mx-auto w-full max-w-[1024px] px-6 py-12 sm:px-10 lg:px-0 lg:py-18">
        <motion.h1
          className="font-body text-4xl font-medium leading-tight tracking-tight text-[hsl(var(--text-100))] sm:text-4xl md:text-[48px] lg:max-w-[50%]"
          data-slot="pass-headline"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          {copy.titlePart1}
          <span className="font-display font-bold italic text-[hsl(var(--accent-yellow))]">
            {copy.titleBrisa}
          </span>
          .
        </motion.h1>

        <motion.div
          className="mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut', delay: 0.15 }}
        >
          <Tabs
            variant="default-pill"
            value={value}
            onValueChange={setValue}
            data-slot="pass-plans-tabs"
          >
            <TabsList>
              <TabsTrigger value="individual" data-slot="tab-individual">
                {copy.tabIndividual}
              </TabsTrigger>
              <TabsTrigger value="familia" data-slot="tab-familia">
                {copy.tabFamilia}
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </motion.div>
      </div>

      {/* Plans grid — cross-fade entre Individual e Família.
          Mobile (< md): scroll-snap horizontal (lote 2 mobile-tweaks-v2). Bleed
          para fora do px-6/px-10 do container via `-mx-6 sm:-mx-10`, mantendo
          scroll-padding equivalente. Cada card ocupa 88% do viewport, deixando
          peek do próximo. Desktop (md+): grid 2/4 colunas como antes. */}
      <div className="mx-auto w-full max-w-[1024px] px-6 py-12 sm:px-10 lg:px-0">
        <AnimatePresence mode="wait">
          {value === 'individual' ? (
            <motion.div
              key="individual"
              variants={panelVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              data-slot="plans-grid-individual"
            >
              <ul
                className="-mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2 scroll-pl-6 sm:-mx-10 sm:px-10 sm:scroll-pl-10 md:mx-0 md:grid md:grid-cols-2 md:overflow-visible md:px-0 md:pb-0 md:scroll-pl-0 lg:grid-cols-4"
                data-slot="plans-list"
              >
                {PLAN_ORDER.map((planKey) => {
                  const plan = copy.plans[planKey];
                  const isHighlighted = planKey === 'premium4';
                  return (
                    <motion.li
                      key={planKey}
                      className="flex w-[88%] shrink-0 snap-start md:w-auto md:shrink"
                      variants={cardVariants}
                    >
                      <CardPlanComplete
                        tag={plan.tag}
                        price={plan.price}
                        priceSubtext={plan.priceSubtext}
                        description={plan.description}
                        infoRows={[
                          { label: copy.infoPeriodo, value: plan.period },
                          { label: copy.infoClubes, value: plan.clubes },
                          { label: copy.infoVisitas, value: plan.visitas },
                        ]}
                        ctaLabel={copy.ctaChoose}
                        onCtaClick={scrollToWaitlist}
                        variant={isHighlighted ? 'highlighted' : 'default'}
                        freeBadge={isHighlighted ? copy.freeBadgePremium4 : undefined}
                        className="w-full"
                        data-event="plan-card-clicked"
                        data-plan-key={planKey}
                        data-surface={`${surface}_individual`}
                      />
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>
          ) : (
            <motion.div
              key="familia"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              className="flex flex-col items-center gap-6 rounded-[var(--radius-xl)] bg-[hsl(var(--text-08))] px-8 py-16 text-center"
              data-slot="plans-grid-familia"
            >
              <h2 className="font-body text-2xl font-medium leading-tight text-[hsl(var(--text-100))]">
                {copy.familiaTitle}
              </h2>
              <p className="max-w-[42ch] font-body text-sm leading-relaxed text-[hsl(var(--text-56))]">
                {copy.familiaBody}
              </p>
              <Button
                type="button"
                onClick={scrollToWaitlist}
                variant="primary-medium"
                size="md"
                data-event="landing-cta"
                data-surface={`${surface}_familia`}
                data-destination="waitlist"
              >
                {copy.familiaCta}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
